import {Modal as ModalPepsico} from '@pepsico-ds/ui';
import {useIntl} from 'react-intl';
import {cn} from '@/helpers/shadcn/utils';
import {MODAL_TYPE, SIZE, VARIANT} from '@/utils/constants/globalStyle';
import React from 'react';
import PropTypes from 'prop-types';

/**
 * Modal component for displaying a customizable modal dialog.
 *
 * @param {Object} props - The properties object.
 * @param {('fluid'|'XL'|'L'|'M'|'S')} [props.type=MODAL_TYPE.FLUID] - The type of the modal.
 * @param {boolean} props.showModal - Flag to show or hide the modal.
 * @param {string} [props.title=''] - The title of the modal.
 * @param {string} [props.content=''] - The content of the modal.
 * @param {boolean} props.isDark - Flag to apply dark theme to the modal.
 * @param {string} props.className - Additional class names for the modal.
 * @param {string} [props.text=''] - Additional text for the modal.
 * @param {string} props.primaryButtonText - Text for the primary button.
 * @param {string} props.secondaryButtonText - Text for the secondary button.
 * @param {Function} props.onCloseModal - Callback function when the modal is closed.
 * @param {Function} props.onClickSecondaryButton - Callback function when the secondary button is clicked.
 * @param {Function} props.onClickPrimaryButton - Callback function when the primary button is clicked.
 * @param {string} [props.classNameWrapper=''] - Additional class names for the modal wrapper.
 * @param {boolean} [props.primaryButtonDisabled=false] - Flag to disable the primary button.
 * @param {boolean} [props.showSecondaryButton=false] - Flag to show or hide the secondary button.
 * @param {boolean} [props.showPrimaryButton=true] - Flag to show or hide the primary button.
 * @param {boolean} [props.showAuxiliaryButton=false] - Flag to show or hide the auxiliary button.
 * @param {boolean} [props.showTertiaryButton=false] - Flag to show or hide the tertiary button.
 * @param {boolean} [props.showLink=false] - Flag to show or hide a link in the modal.
 * @param {string} [props.id=''] - Optional - The id for the modal.
 * @param {Object} [props.primaryButtonProps] - Optional - Additional props for primary button (e.g., {iconLeading: 'file_download'}).
 * @param {Object} [props.secondaryButtonProps] - Optional - Additional props for secondary button (e.g., {iconLeading: 'close'}).
 * @returns {JSX.Element} The rendered Modal component.
 */
const Modal = ({
  type = MODAL_TYPE.FLUID,
  showModal,
  title = '',
  content = '',
  isDark,
  className,
  text = '',
  primaryButtonText,
  secondaryButtonText,
  onCloseModal,
  onClickSecondaryButton,
  onClickPrimaryButton,
  classNameWrapper = '',
  primaryButtonDisabled = false,
  showSecondaryButton = false,
  showPrimaryButton = true,
  showAuxiliaryButton = false,
  showTertiaryButton = false,
  showLink = false,
  tertiaryButtonText,
  auxiliaryButtonText,
  onClickTertiaryButton,
  onClickAuxiliaryButton,
  footerProps,
  id = '',
  secondaryButtonDisabled = false,
  hideCloseButton = false,
  primaryButtonProps,
  secondaryButtonProps,
  tertiaryButtonProps,
  auxiliaryButtonProps
}) => {
  const {formatMessage} = useIntl();

  return (
    <ModalPepsico
      data-testid={`modal` + (id ? `_${id}` : '')}
      id={`modal` + (id ? `_${id}` : '')}
      type={type}
      showModal={showModal}
      isDark={isDark}
      footerProps={footerProps}
      title={title}
      text={text}
      className={className}
      showLink={showLink}
      onCloseModal={onCloseModal}
      hideCloseButton={hideCloseButton}
      showTertiaryButton={showTertiaryButton}
      secondaryButtonProps={{
        ...(secondaryButtonProps?.iconLeading ||
        secondaryButtonProps?.iconTrailing
          ? {}
          : {iconTrailing: 'none'}),
        size: SIZE.MEDIUM,
        text:
          secondaryButtonText ?? formatMessage({id: 'GENERAL.CANCEL_BUTTON'}),
        variant: VARIANT.SECONDARY,
        className: cn(
          'w-fit',
          secondaryButtonProps?.iconLeading ||
            secondaryButtonProps?.iconTrailing
            ? ''
            : '[&>span]:!hidden'
        ),
        onClick: onClickSecondaryButton,
        disabled: secondaryButtonDisabled,
        ...secondaryButtonProps
      }}
      primaryButtonProps={{
        ...(primaryButtonProps?.iconLeading || primaryButtonProps?.iconTrailing
          ? {}
          : {iconTrailing: 'none'}),
        size: SIZE.MEDIUM,
        text: primaryButtonText,
        variant: VARIANT.PRIMARY,
        onClick: onClickPrimaryButton,
        className: cn(
          'w-fit',
          primaryButtonProps?.iconLeading || primaryButtonProps?.iconTrailing
            ? ''
            : '[&>span]:!hidden',
          primaryButtonText ? '' : 'hidden'
        ), // ml-auto removed please add specific class via UI schema; it's affecting Media Upload
        disabled: primaryButtonDisabled,
        ...primaryButtonProps
      }}
      tertiaryButtonProps={{
        onClick: onClickTertiaryButton,
        size: 'medium',
        text: tertiaryButtonText,
        variant: 'tertiary',
        ...tertiaryButtonProps
      }}
      auxiliaryButtonProps={{
        onClick: onClickAuxiliaryButton,
        size: 'medium',
        text: auxiliaryButtonText,
        variant: 'auxiliary',
        ...auxiliaryButtonProps
      }}
      showSecondaryButton={showSecondaryButton}
      showPrimaryButton={showPrimaryButton}
      showAuxiliaryButton={showAuxiliaryButton}
      data-testId='modal-component'>
      <div
        data-testid='modal-wrapper'
        className={cn('w-full overflow-y-auto', classNameWrapper)}>
        {content}
      </div>
    </ModalPepsico>
  );
};

Modal.propTypes = {
  type: PropTypes.oneOf(['fluid', 'XL', 'L', 'M', 'S']),
  showModal: PropTypes.bool.isRequired,
  title: PropTypes.string,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isDark: PropTypes.bool.isRequired,
  className: PropTypes.string,
  text: PropTypes.string,
  primaryButtonText: PropTypes.string.isRequired,
  secondaryButtonText: PropTypes.string,
  onCloseModal: PropTypes.func.isRequired,
  onClickSecondaryButton: PropTypes.func.isRequired,
  onClickPrimaryButton: PropTypes.func.isRequired,
  classNameWrapper: PropTypes.string,
  primaryButtonDisabled: PropTypes.bool,
  showSecondaryButton: PropTypes.bool,
  showPrimaryButton: PropTypes.bool,
  showAuxiliaryButton: PropTypes.bool,
  showTertiaryButton: PropTypes.bool,
  showLink: PropTypes.bool,
  id: PropTypes.string,
  tertiaryButtonText: PropTypes.string,
  auxiliaryButtonText: PropTypes.string,
  onClickTertiaryButton: PropTypes.func,
  onClickAuxiliaryButton: PropTypes.func,
  footerProps: PropTypes.object,
  secondaryButtonDisabled: PropTypes.bool,
  hideCloseButton: PropTypes.bool,
  primaryButtonProps: PropTypes.object,
  secondaryButtonProps: PropTypes.object,
  tertiaryButtonProps: PropTypes.object,
  auxiliaryButtonProps: PropTypes.object
};

export default React.memo(Modal);
